import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enGB from './en-GB';
import enUS from './en-US';
import enIN from './en-IN';
import hiIN from './hi-IN';
import frFR from './fr-FR';
import deDE from './de-DE';
import esES from './es-ES';
import arAE from './ar-AE';
import ptBR from './pt-BR';
import zhCN from './zh-CN';
import jaJP from './ja-JP';
import koKR from './ko-KR';
import idID from './id-ID';

export const SUPPORTED_LOCALES = [
  // India-first (primary market); other countries are secondary.
  { code: 'en-IN', label: 'English (India)',       flag: '🇮🇳', currency: 'INR', dir: 'ltr' },
  { code: 'hi-IN', label: 'हिन्दी (भारत)',          flag: '🇮🇳', currency: 'INR', dir: 'ltr' },
  { code: 'en-GB', label: 'English (UK)',          flag: '🇬🇧', currency: 'GBP', dir: 'ltr' },
  { code: 'en-US', label: 'English (US)',          flag: '🇺🇸', currency: 'USD', dir: 'ltr' },
  { code: 'en-CA', label: 'English (Canada)',      flag: '🇨🇦', currency: 'CAD', dir: 'ltr' },
  { code: 'en-AU', label: 'English (Australia)',   flag: '🇦🇺', currency: 'AUD', dir: 'ltr' },
  { code: 'en-SG', label: 'English (Singapore)',   flag: '🇸🇬', currency: 'SGD', dir: 'ltr' },
  { code: 'en-NG', label: 'English (Nigeria)',     flag: '🇳🇬', currency: 'NGN', dir: 'ltr' },
  { code: 'en-KE', label: 'English (Kenya)',       flag: '🇰🇪', currency: 'KES', dir: 'ltr' },
  { code: 'en-ZA', label: 'English (South Africa)',flag: '🇿🇦', currency: 'ZAR', dir: 'ltr' },
  { code: 'en-GH', label: 'English (Ghana)',       flag: '🇬🇭', currency: 'GHS', dir: 'ltr' },
  { code: 'fr-FR', label: 'Français (France)',     flag: '🇫🇷', currency: 'EUR', dir: 'ltr' },
  { code: 'de-DE', label: 'Deutsch (Deutschland)', flag: '🇩🇪', currency: 'EUR', dir: 'ltr' },
  { code: 'es-ES', label: 'Español (España)',      flag: '🇪🇸', currency: 'EUR', dir: 'ltr' },
  { code: 'es-MX', label: 'Español (México)',      flag: '🇲🇽', currency: 'MXN', dir: 'ltr' },
  { code: 'pt-BR', label: 'Português (Brasil)',    flag: '🇧🇷', currency: 'BRL', dir: 'ltr' },
  { code: 'zh-CN', label: '中文 (中国)',             flag: '🇨🇳', currency: 'CNY', dir: 'ltr' },
  { code: 'zh-TW', label: '中文 (台灣)',             flag: '🇹🇼', currency: 'TWD', dir: 'ltr' },
  { code: 'ja-JP', label: '日本語',                 flag: '🇯🇵', currency: 'JPY', dir: 'ltr' },
  { code: 'ko-KR', label: '한국어',                 flag: '🇰🇷', currency: 'KRW', dir: 'ltr' },
  { code: 'id-ID', label: 'Bahasa Indonesia',      flag: '🇮🇩', currency: 'IDR', dir: 'ltr' },
  { code: 'ar-AE', label: 'العربية',               flag: '🇦🇪', currency: 'AED', dir: 'rtl' },
];

export const CURRENCIES = {
  INR: { symbol: '₹',    name: 'Indian Rupee',         locale: 'en-IN', flag: '🇮🇳' },
  GBP: { symbol: '£',    name: 'British Pound',       locale: 'en-GB', flag: '🇬🇧' },
  USD: { symbol: '$',    name: 'US Dollar',            locale: 'en-US', flag: '🇺🇸' },
  EUR: { symbol: '€',    name: 'Euro',                 locale: 'fr-FR', flag: '🇪🇺' },
  CAD: { symbol: 'CA$',  name: 'Canadian Dollar',      locale: 'en-CA', flag: '🇨🇦' },
  AUD: { symbol: 'A$',   name: 'Australian Dollar',    locale: 'en-AU', flag: '🇦🇺' },
  JPY: { symbol: '¥',    name: 'Japanese Yen',         locale: 'ja-JP', flag: '🇯🇵' },
  SGD: { symbol: 'S$',   name: 'Singapore Dollar',     locale: 'en-SG', flag: '🇸🇬' },
  AED: { symbol: 'AED',  name: 'UAE Dirham',           locale: 'ar-AE', flag: '🇦🇪' },
  ZAR: { symbol: 'R',    name: 'South African Rand',   locale: 'en-ZA', flag: '🇿🇦' },
  BRL: { symbol: 'R$',   name: 'Brazilian Real',       locale: 'pt-BR', flag: '🇧🇷' },
  MXN: { symbol: 'MX$',  name: 'Mexican Peso',         locale: 'es-MX', flag: '🇲🇽' },
  NGN: { symbol: '₦',    name: 'Nigerian Naira',       locale: 'en-NG', flag: '🇳🇬' },
  KES: { symbol: 'KSh',  name: 'Kenyan Shilling',      locale: 'en-KE', flag: '🇰🇪' },
  IDR: { symbol: 'Rp',   name: 'Indonesian Rupiah',    locale: 'id-ID', flag: '🇮🇩' },
  KRW: { symbol: '₩',    name: 'South Korean Won',     locale: 'ko-KR', flag: '🇰🇷' },
  GHS: { symbol: 'GH₵',  name: 'Ghanaian Cedi',        locale: 'en-GH', flag: '🇬🇭' },
};

// Country presets — supply locale defaults when user selects their country
export const COUNTRY_PRESETS = {
  GB: { country: 'United Kingdom',      currency: 'GBP', locale: 'en-GB', language: 'en', flag: '🇬🇧', timezone: 'Europe/London',
        cityPlaceholder: 'e.g. London, Manchester', buildingPlaceholder: 'e.g. Elm Court', postalCodeLabel: 'Postcode', postalCodePlaceholder: 'e.g. SW1A 1AA' },
  US: { country: 'United States',       currency: 'USD', locale: 'en-US', language: 'en', flag: '🇺🇸', timezone: 'America/New_York',
        cityPlaceholder: 'e.g. New York, Chicago',   buildingPlaceholder: 'e.g. Riverside Apts', postalCodeLabel: 'ZIP Code', postalCodePlaceholder: 'e.g. 90210' },
  IN: { country: 'India',               currency: 'INR', locale: 'en-IN', language: 'en', flag: '🇮🇳', timezone: 'Asia/Kolkata',
        cityPlaceholder: 'e.g. Mumbai, Delhi',        buildingPlaceholder: 'e.g. Shree Krishna Society', postalCodeLabel: 'PIN Code', postalCodePlaceholder: 'e.g. 400001' },
  CA: { country: 'Canada',              currency: 'CAD', locale: 'en-CA', language: 'en', flag: '🇨🇦', timezone: 'America/Toronto',
        cityPlaceholder: 'e.g. Toronto, Vancouver',   buildingPlaceholder: 'e.g. Maple Grove Apts', postalCodeLabel: 'Postal Code', postalCodePlaceholder: 'e.g. M5V 2T6' },
  AU: { country: 'Australia',           currency: 'AUD', locale: 'en-AU', language: 'en', flag: '🇦🇺', timezone: 'Australia/Sydney',
        cityPlaceholder: 'e.g. Sydney, Melbourne',    buildingPlaceholder: 'e.g. Harbour View', postalCodeLabel: 'Postcode', postalCodePlaceholder: 'e.g. 2000' },
  SG: { country: 'Singapore',           currency: 'SGD', locale: 'en-SG', language: 'en', flag: '🇸🇬', timezone: 'Asia/Singapore',
        cityPlaceholder: 'Singapore',                 buildingPlaceholder: 'e.g. Punggol HDB Block 123', postalCodeLabel: 'Postal Code', postalCodePlaceholder: 'e.g. 560123' },
  NG: { country: 'Nigeria',             currency: 'NGN', locale: 'en-NG', language: 'en', flag: '🇳🇬', timezone: 'Africa/Lagos',
        cityPlaceholder: 'e.g. Lagos, Abuja',         buildingPlaceholder: 'e.g. Victoria Garden City', postalCodeLabel: 'Postal Code', postalCodePlaceholder: 'e.g. 100001' },
  KE: { country: 'Kenya',               currency: 'KES', locale: 'en-KE', language: 'en', flag: '🇰🇪', timezone: 'Africa/Nairobi',
        cityPlaceholder: 'e.g. Nairobi, Mombasa',     buildingPlaceholder: 'e.g. Westlands Estate', postalCodeLabel: 'Postal Code', postalCodePlaceholder: 'e.g. 00100' },
  ZA: { country: 'South Africa',        currency: 'ZAR', locale: 'en-ZA', language: 'en', flag: '🇿🇦', timezone: 'Africa/Johannesburg',
        cityPlaceholder: 'e.g. Cape Town, Johannesburg', buildingPlaceholder: 'e.g. Century City', postalCodeLabel: 'Postal Code', postalCodePlaceholder: 'e.g. 8001' },
  GH: { country: 'Ghana',               currency: 'GHS', locale: 'en-GH', language: 'en', flag: '🇬🇭', timezone: 'Africa/Accra',
        cityPlaceholder: 'e.g. Accra, Kumasi',         buildingPlaceholder: 'e.g. East Legon', postalCodeLabel: 'Postal Code', postalCodePlaceholder: '' },
  FR: { country: 'France',              currency: 'EUR', locale: 'fr-FR', language: 'fr', flag: '🇫🇷', timezone: 'Europe/Paris',
        cityPlaceholder: 'p.ex. Paris, Lyon',          buildingPlaceholder: 'p.ex. Résidence Les Lilas', postalCodeLabel: 'Code Postal', postalCodePlaceholder: 'p.ex. 75001' },
  DE: { country: 'Germany',             currency: 'EUR', locale: 'de-DE', language: 'de', flag: '🇩🇪', timezone: 'Europe/Berlin',
        cityPlaceholder: 'z.B. Berlin, München',       buildingPlaceholder: 'z.B. Goethestraße 5', postalCodeLabel: 'Postleitzahl', postalCodePlaceholder: 'z.B. 10115' },
  ES: { country: 'Spain',               currency: 'EUR', locale: 'es-ES', language: 'es', flag: '🇪🇸', timezone: 'Europe/Madrid',
        cityPlaceholder: 'p.ej. Madrid, Barcelona',    buildingPlaceholder: 'p.ej. C/ Gran Vía 1', postalCodeLabel: 'Código Postal', postalCodePlaceholder: 'p.ej. 28001' },
  MX: { country: 'Mexico',              currency: 'MXN', locale: 'es-MX', language: 'es', flag: '🇲🇽', timezone: 'America/Mexico_City',
        cityPlaceholder: 'p.ej. Ciudad de México',     buildingPlaceholder: 'p.ej. Condesa Depto 3B', postalCodeLabel: 'Código Postal', postalCodePlaceholder: 'p.ej. 06600' },
  BR: { country: 'Brazil',              currency: 'BRL', locale: 'pt-BR', language: 'pt', flag: '🇧🇷', timezone: 'America/Sao_Paulo',
        cityPlaceholder: 'ex. São Paulo, Rio de Janeiro', buildingPlaceholder: 'ex. Condomínio Vila Nova', postalCodeLabel: 'CEP', postalCodePlaceholder: 'ex. 01310-100' },
  CN: { country: 'China',               currency: 'CNY', locale: 'zh-CN', language: 'zh', flag: '🇨🇳', timezone: 'Asia/Shanghai',
        cityPlaceholder: '如 上海、北京',               buildingPlaceholder: '如 阳光花园3号楼', postalCodeLabel: '邮政编码', postalCodePlaceholder: '如 200001' },
  JP: { country: 'Japan',               currency: 'JPY', locale: 'ja-JP', language: 'ja', flag: '🇯🇵', timezone: 'Asia/Tokyo',
        cityPlaceholder: '例）東京、大阪',              buildingPlaceholder: '例）渋谷区松濤1丁目', postalCodeLabel: '郵便番号', postalCodePlaceholder: '例）100-0001' },
  KR: { country: 'South Korea',         currency: 'KRW', locale: 'ko-KR', language: 'ko', flag: '🇰🇷', timezone: 'Asia/Seoul',
        cityPlaceholder: '예) 서울, 부산',              buildingPlaceholder: '예) 강남구 아파트 101동', postalCodeLabel: '우편번호', postalCodePlaceholder: '예) 06141' },
  ID: { country: 'Indonesia',           currency: 'IDR', locale: 'id-ID', language: 'id', flag: '🇮🇩', timezone: 'Asia/Jakarta',
        cityPlaceholder: 'mis. Jakarta, Bandung',       buildingPlaceholder: 'mis. Apartemen Kuningan', postalCodeLabel: 'Kode Pos', postalCodePlaceholder: 'mis. 10110' },
  AE: { country: 'United Arab Emirates',currency: 'AED', locale: 'ar-AE', language: 'ar', flag: '🇦🇪', timezone: 'Asia/Dubai',
        cityPlaceholder: 'مثل دبي، أبوظبي',            buildingPlaceholder: 'مثل برج خليفة', postalCodeLabel: 'الرمز البريدي', postalCodePlaceholder: '' },
};

// All countries list for dropdowns (sorted by region)
export const COUNTRIES_LIST = [
  // English-speaking
  { code:'GB', name:'United Kingdom' }, { code:'US', name:'United States' },
  { code:'CA', name:'Canada' },         { code:'AU', name:'Australia' },
  { code:'NZ', name:'New Zealand' },    { code:'SG', name:'Singapore' },
  { code:'NG', name:'Nigeria' },        { code:'KE', name:'Kenya' },
  { code:'ZA', name:'South Africa' },   { code:'GH', name:'Ghana' },
  // South Asia
  { code:'IN', name:'India' }, { code:'PK', name:'Pakistan' },
  { code:'BD', name:'Bangladesh' }, { code:'LK', name:'Sri Lanka' },
  // Europe
  { code:'FR', name:'France' }, { code:'DE', name:'Germany' },
  { code:'ES', name:'Spain' },  { code:'IT', name:'Italy' },
  { code:'NL', name:'Netherlands' }, { code:'PT', name:'Portugal' },
  { code:'SE', name:'Sweden' }, { code:'NO', name:'Norway' },
  // Americas
  { code:'MX', name:'Mexico' }, { code:'BR', name:'Brazil' },
  { code:'AR', name:'Argentina' }, { code:'CO', name:'Colombia' },
  // Asia
  { code:'CN', name:'China' },  { code:'TW', name:'Taiwan' },
  { code:'JP', name:'Japan' },  { code:'KR', name:'South Korea' },
  { code:'ID', name:'Indonesia' }, { code:'TH', name:'Thailand' },
  { code:'VN', name:'Vietnam' }, { code:'MY', name:'Malaysia' },
  { code:'PH', name:'Philippines' },
  // Middle East
  { code:'AE', name:'United Arab Emirates' }, { code:'SA', name:'Saudi Arabia' },
  { code:'QA', name:'Qatar' }, { code:'KW', name:'Kuwait' },
  // Other
  { code:'OTHER', name:'Other' },
];

// Currency formatter using Intl.NumberFormat
export function formatCurrency(amount, currency = 'INR', locale = 'en-IN') {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency,
      maximumFractionDigits: ['JPY','KRW','IDR','VND'].includes(currency) ? 0 : 2,
    }).format(amount);
  } catch {
    const symbol = CURRENCIES[currency]?.symbol || currency;
    return `${symbol}${amount}`;
  }
}

export function formatDate(dateString, locale = 'en-IN', options = {}) {
  if (!dateString) return '';
  try {
    return new Intl.DateTimeFormat(locale, { year:'numeric', month:'short', day:'numeric', ...options }).format(new Date(dateString));
  } catch { return dateString; }
}

export function formatTime(dateString, locale = 'en-IN', timezone) {
  if (!dateString) return '';
  try {
    return new Intl.DateTimeFormat(locale, { hour:'2-digit', minute:'2-digit', ...(timezone ? { timeZone: timezone } : {}) }).format(new Date(dateString));
  } catch { return dateString; }
}

// Detect locale from browser or stored user prefs
export function detectLocale(storedLocale) {
  if (storedLocale && SUPPORTED_LOCALES.find(l => l.code === storedLocale)) return storedLocale;
  const nav = (typeof navigator !== 'undefined' && navigator.language) || 'en-IN';
  if (nav.startsWith('hi')) return 'hi-IN';
  if (nav.startsWith('fr')) return 'fr-FR';
  if (nav.startsWith('de')) return 'de-DE';
  if (nav.startsWith('es')) return 'es-ES';
  if (nav.startsWith('ar')) return 'ar-AE';
  if (nav.startsWith('pt')) return 'pt-BR';
  if (nav.startsWith('zh')) return 'zh-CN';
  if (nav.startsWith('ja')) return 'ja-JP';
  if (nav.startsWith('ko')) return 'ko-KR';
  if (nav.startsWith('id')) return 'id-ID';
  if (nav.startsWith('en-US')) return 'en-US';
  if (nav.startsWith('en-IN')) return 'en-IN';
  if (nav.startsWith('en-CA')) return 'en-CA';
  if (nav.startsWith('en-AU')) return 'en-AU';
  if (nav.startsWith('en-SG')) return 'en-SG';
  if (nav.startsWith('en-GB')) return 'en-GB';
  // India-first: any unrecognised locale defaults to India.
  return 'en-IN';
}

// Lazy-load locale file for new locales (these share en-GB translations as fallback)
const extraLocaleResources = {
  'en-CA': enGB, 'en-AU': enGB, 'en-SG': enGB, 'en-NG': enGB, 'en-KE': enGB, 'en-ZA': enGB, 'en-GH': enGB,
  'es-MX': esES, 'zh-TW': zhCN,
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      'en-GB': enGB, 'en-US': enUS, 'en-IN': enIN, 'hi-IN': hiIN,
      'fr-FR': frFR, 'de-DE': deDE, 'es-ES': esES, 'ar-AE': arAE,
      'pt-BR': ptBR, 'zh-CN': zhCN, 'ja-JP': jaJP, 'ko-KR': koKR, 'id-ID': idID,
      ...extraLocaleResources,
    },
    lng: detectLocale(),
    fallbackLng: 'en-GB',
    interpolation: { escapeValue: false },
  });

export default i18n;
